import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { usePASAdminAuth } from '../../context/PASAdminAuthContext';
import { saveElementPdf } from '../../utils/savePdf';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Printer, XCircle, ExternalLink, Link2, Download } from 'lucide-react';
import { TreasuryCheckFace } from '../../components/pas/TreasuryCheckPrintParts';
import { toTreasuryFace } from '../../components/check/cryptoTreasuryFace';
import { CheckVoucher } from '../../components/check/CheckVoucher';
import { TaxReceipt } from '../../components/check/TaxReceipt';
import '../../components/check/check-print.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Perforation = () => (
  <div className="check-perf"><span>DETACH HERE</span></div>
);

const ViewCheck = () => {
  const navigate = useNavigate();
  const { checkNumber } = useParams();
  const isAdmin = useLocation().pathname.startsWith('/pas/admin/');
  const clientAuth = usePASClientAuth();
  const adminAuth = usePASAdminAuth();
  const { token, loading: authLoading } = isAdmin ? adminAuth : clientAuth;
  const pasClient = isAdmin ? adminAuth.token : clientAuth.pasClient;
  const back = isAdmin ? '/pas/admin/dashboard' : '/pas/dashboard';
  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchoring, setAnchoring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sigUrl, setSigUrl] = useState(null);

  useEffect(() => {
    if (isAdmin || !printData?.has_signature_image || !token) return undefined;
    let url;
    axios.get(`${API}/crypto-check/${checkNumber}/signature`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then(res => { url = URL.createObjectURL(res.data); setSigUrl(url); })
      .catch(() => setSigUrl(null));
    return () => url && URL.revokeObjectURL(url);
  }, [printData, token, checkNumber]);

  useEffect(() => {
    if (!authLoading && !pasClient) navigate(isAdmin ? '/pas/admin/login' : '/pas/login');
  }, [authLoading, pasClient, navigate, isAdmin]);

  const fetchCheckData = async () => {
    try {
      const response = await axios.get(`${API}/crypto-check/${isAdmin ? 'admin/' : ''}${checkNumber}/print-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrintData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load check');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && checkNumber) fetchCheckData();
  }, [token, checkNumber]);

  const recordOnChain = async () => {
    setAnchoring(true);
    try {
      await axios.post(`${API}/crypto-check/${checkNumber}/anchor`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('E-signature recorded on Sepolia/Mainnet');
      await fetchCheckData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record on blockchain');
    } finally {
      setAnchoring(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-red-400" data-testid="check-error">{error}</p>
          <button onClick={() => navigate(back)} className="mt-4 px-4 py-2 bg-slate-800 text-white rounded-lg">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 tc-print-root">
      <header className="bg-slate-800 border-b border-slate-700 print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(back)} className="p-2 text-slate-400 hover:text-white transition-colors" data-testid="check-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">Check #{checkNumber}</h1>
              <p className="text-xs text-slate-400">
                Status: <span className={printData.status === 'issued' ? 'text-emerald-400' : 'text-red-400'} data-testid="check-status">{printData.status?.toUpperCase()}</span>
                {' · '}US Letter, 3-part (check, voucher, 1099-B receipt)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {printData.etherscan_url ? (
              <a href={printData.etherscan_url} target="_blank" rel="noopener noreferrer" data-testid="check-etherscan-link"
                className="px-4 py-2 border border-slate-600 text-slate-200 hover:text-white rounded-lg text-sm flex items-center gap-2">
                Etherscan record <ExternalLink className="w-4 h-4" />
              </a>
            ) : !isAdmin && (
              <button onClick={recordOnChain} disabled={anchoring} data-testid="check-record-onchain-btn"
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                <Link2 className="w-4 h-4" /> {anchoring ? 'Recording...' : 'Record e-signature on Sepolia/Mainnet'}
              </button>
            )}
            <button disabled={saving} data-testid="check-pdf-btn"
              onClick={async () => { setSaving(true); try { await saveElementPdf(document.querySelector('[data-testid="check-sheet"]'), `Check-${checkNumber}.pdf`); toast.success('PDF saved'); } catch (e) { toast.error('Could not create the PDF'); } setSaving(false); }}
              className="px-4 py-2 border border-slate-600 text-slate-200 hover:text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
              <Download className="w-4 h-4" /> {saving ? 'Saving…' : 'Save PDF'}
            </button>
            <button onClick={() => window.print()} data-testid="check-print-btn"
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2">
              <Printer className="w-5 h-5" /> Print Check
            </button>
          </div>
        </div>
      </header>

      <div className="overflow-x-auto py-10 print:p-0">
        <div className="check-sheet" data-testid="check-sheet">
          <TreasuryCheckFace sigUrl={sigUrl} signer={printData.e_signature?.signer}
            verifyUrl={`${window.location.origin}/verify-check/${printData.check.check_number}?${new URLSearchParams({ t: printData.claim_token, ...(printData.bank_qr ? { v: 'BETHCHK1', ...printData.bank_qr } : {}) })}`}
            c={{ ...toTreasuryFace({
              number: printData.check.check_number, date: printData.check.date,
              amount: Number(String(printData.check.amount_numeric).replace(/[$,]/g, '')),
              payee: printData.check.payee_name, payeeAddress: printData.check.payee_address, memo: printData.check.memo,
              drawer: printData.check.drawer_name, wallet: printData.check.drawer_wallet, hash: printData.check.crypto_stamp?.full_hash,
              network: printData.check.network,
              settle: `Settled as ${printData.check.beth_amount} at ${printData.check.exchange_rate_display}`,
            }), amount_words: printData.check.amount_words, tx_hash: printData.check.etherscan_url?.split('/tx/')[1],
            micr: printData.bank_qr ? { routing: printData.bank_qr.rt, account: printData.bank_qr.acct } : undefined }} />
          <Perforation />
          <CheckVoucher data={printData} />
          <Perforation />
          <TaxReceipt data={printData} />
        </div>
      </div>
    </div>
  );
};

export default ViewCheck;
